import { useEffect, useState } from "react";
import { Link } from "react-router-dom";

import SkeletonCard from "components/layout/loading/SkeletonCard";
import PresetDateRangeInput from "components/layout/inputs/PresetDateRangeInput";
import { getMissionOverview } from "lib/fleetMissionStorage/FleetMissionStorageClient";
import { getScheduledJobCount } from "lib/fleetMissionStorage/FleetScheduleClient";
import { resolveMissionOverviewDateRange } from "lib/fleetMissionStorage/missionOverviewRange";
import { useOrgFleet } from "providers/OrgFleetProvider";
import { useMissionOverviewRange } from "providers/MissionOverviewRangeProvider";

const formatAverageDuration = (seconds) => {
    if (seconds === null || seconds === undefined || !Number.isFinite(Number(seconds))) {
        return "—";
    }
    const totalSeconds = Math.max(0, Math.round(Number(seconds)));
    if (totalSeconds >= 3600) {
        const hours = Math.floor(totalSeconds / 3600);
        const minutes = Math.floor((totalSeconds % 3600) / 60);
        return minutes > 0 ? `${hours}h ${minutes}m` : `${hours}h`;
    }
    if (totalSeconds >= 60) return `${Math.round(totalSeconds / 60)} min`;
    return `${totalSeconds} sec`;
};

const SummaryCard = ({ value, description, linkTo, linkLabel }) => {
    return (
        <div className="relative flex h-full w-full flex-col items-center justify-center space-y-1 rounded-r10 bg-black90 px-2">
            <p className="h3">{value}</p>
            <p className="text-h5 max-w-[75ch] text-center leading-none text-black40">
                {description}
            </p>
            {linkTo && linkLabel && (
                <Link
                    className="text-h5 absolute right-2 top-2 text-primary2 hover:text-primary"
                    to={linkTo}
                >
                    {linkLabel}
                </Link>
            )}
        </div>
    );
};

const MissionsSummary = ({ className = "" }) => {
    const { proxyHostname } = useOrgFleet();
    const [overview, setOverview] = useState(null);
    const [scheduleCount, setScheduleCount] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const { rangeSettings, setRangeSettings } = useMissionOverviewRange();
    const dateRange = resolveMissionOverviewDateRange(rangeSettings);
    const customRangeInvalid = rangeSettings.selection === "custom" && dateRange === null;

    useEffect(() => {
        if (!proxyHostname || customRangeInvalid) {
            setOverview(null);
            if (!proxyHostname) setScheduleCount(null);
            setIsLoading(false);
            return;
        }

        let cancelled = false;
        setIsLoading(true);
        Promise.all([
            getMissionOverview(proxyHostname, dateRange ?? undefined),
            getScheduledJobCount(proxyHostname),
        ])
            .then(([overviewResponse, scheduleResponse]) => {
                if (!cancelled) {
                    setOverview(overviewResponse);
                    setScheduleCount(scheduleResponse);
                }
            })
            .catch((error) => {
                if (!cancelled) {
                    console.error("Failed to load mission overview:", error);
                    setOverview(null);
                    setScheduleCount(null);
                }
            })
            .finally(() => {
                if (!cancelled) setIsLoading(false);
            });
        return () => {
            cancelled = true;
        };
    }, [proxyHostname, dateRange?.startDate, dateRange?.endDate, customRangeInvalid]);

    const completedRate =
        overview && overview.totalRuns > 0
            ? `${Math.round((overview.completedRuns / overview.totalRuns) * 100)}%`
            : overview
            ? "0%"
            : "—";

    return (
        <section className={`flex min-h-0 w-full flex-col ${className}`}>
            <div className="mb-2 flex min-h-10 flex-wrap items-center justify-between gap-2">
                <h3 className="shrink-0">Mission Overview</h3>
                <PresetDateRangeInput value={rangeSettings} onChange={setRangeSettings} />
            </div>
            <div className="grid min-h-0 flex-1 grid-cols-3 gap-2">
                {isLoading ? (
                    <SkeletonCard />
                ) : (
                    <SummaryCard
                        value={overview?.totalMissions ?? "—"}
                        description="Missions"
                        linkTo="/fleet/dashboard/missions"
                        linkLabel="View Missions"
                    />
                )}
                {isLoading ? (
                    <SkeletonCard />
                ) : (
                    <SummaryCard
                        value={scheduleCount ?? "—"}
                        description="Schedules"
                        linkTo="/fleet/dashboard/schedules"
                        linkLabel="View Schedules"
                    />
                )}
                {isLoading ? (
                    <SkeletonCard />
                ) : (
                    <SummaryCard
                        value={overview?.totalRuns ?? "—"}
                        description="Logs"
                        linkTo="/fleet/dashboard/log/mission"
                        linkLabel="View Logs"
                    />
                )}
            </div>
            <div className="mt-2 grid min-h-0 flex-1 grid-cols-3 gap-2">
                {isLoading ? (
                    <SkeletonCard />
                ) : (
                    <SummaryCard
                        value={overview?.totalRuns ?? "—"}
                        description={"Missions Performed"}
                    />
                )}
                {isLoading ? (
                    <SkeletonCard />
                ) : (
                    <SummaryCard value={completedRate} description={"Mission Complete Rate"} />
                )}
                {isLoading ? (
                    <SkeletonCard />
                ) : (
                    <SummaryCard
                        value={formatAverageDuration(overview?.averageDurationSeconds)}
                        description={"Average Mission Length"}
                    />
                )}
            </div>
        </section>
    );
};

export default MissionsSummary;
